import { SCALES, MeasurementSystem, units } from '@seddi/core';
import { LogoTechnique } from '@seddi/scene';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import type { StyleLogoRegion } from '../../api/styleAggregator';
import type { MockupLogo } from '../../types/mockupLogos';
import { isInside as isLogoInside } from '../../viewer/logoBounds';
import { getInitialTransformFromRegion } from '../GraphicsPanel/logos/logoPlacement';
import { calculateLogoSizeForRegion } from '../GraphicsPanel/logos/logoSizingStrategies';
import {
  updateGraphicDisplayName,
  updateGraphicRegion,
  updateGraphicSize,
  setGraphicTransform,
  updateGraphicTechnique,
  updateGraphicCustomTechnique,
} from '../../store/appSlice';

const SCALE = SCALES.m;
const MIN_LOGO_SIZE = 0.001;
const SCALE_FACTOR = 1.05;

export type DropdownOption = { id: number; value: string; label: string };

const buildOptions = (items: { value: string; label: string }[]): DropdownOption[] =>
  items.map((item, i) => ({ id: i, ...item }));

export const TECHNIQUE_OPTIONS = buildOptions([
  { value: LogoTechnique.Rubber, label: 'Plastisol' },
  { value: LogoTechnique.Waterbased, label: 'Waterbased' },
  { value: LogoTechnique.Glitter, label: 'Glitter' },
  { value: LogoTechnique.HighDensity, label: 'High Density' },
  { value: LogoTechnique.Puff, label: 'Puff' },
  { value: LogoTechnique.HeatTransfer, label: 'Heat Transfer' },
  { value: LogoTechnique.DirectToGarment, label: 'Direct To Garment' },
  { value: LogoTechnique.Embroidery, label: 'Embroidery' },
  { value: LogoTechnique.Patch, label: 'Patch' },
  { value: LogoTechnique.Custom, label: 'Custom' },
]);

type UseGraphicPropertiesProps = {
  logo: MockupLogo;
  regionOptions: DropdownOption[];
  styleRegions: StyleLogoRegion[];
  units?: MeasurementSystem;
};

const is2DProduct = false; // TODO: derive from style if needed

export const useGraphicProperties = ({
  logo,
  regionOptions,
  styleRegions,
  units: ms = MeasurementSystem.Imperial,
}: UseGraphicPropertiesProps) => {
  const dispatch = useDispatch();

  const { toDisplay, toUniversal, unitSuffix } = useMemo(
    () => ({
      toDisplay: (universal: number) => units.translateToUserUnits(ms, SCALE, universal, 2),
      toUniversal: (display: number) => units.translateToUniversalUnits(ms, SCALE, display),
      unitSuffix: units.obtainShortUnits(ms, SCALE),
    }),
    [ms],
  );

  const [inputValues, setInputValues] = useState<{ x: string; y: string }>(() => ({
    x: String(toDisplay(logo.size.x) ?? ''),
    y: String(toDisplay(logo.size.y) ?? ''),
  }));
  const [focusedAxis, setFocusedAxis] = useState<'x' | 'y' | null>(null);
  const [customTechnique, setCustomTechnique] = useState(logo.customTechnique ?? '');

  useEffect(() => {
    setCustomTechnique(logo.customTechnique ?? '');
  }, [logo.id, logo.customTechnique]);

  useEffect(() => {
    if (focusedAxis !== null) return;
    setInputValues({
      x: String(toDisplay(logo.size.x) ?? ''),
      y: String(toDisplay(logo.size.y) ?? ''),
    });
  }, [logo.size.x, logo.size.y, focusedAxis, toDisplay]);

  const handleNameBlur = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (trimmed) {
        dispatch(updateGraphicDisplayName({ id: logo.id, displayName: trimmed }));
      }
    },
    [dispatch, logo.id],
  );

  const handleSizeInputChange = useCallback((axis: 'x' | 'y', value: string) => {
    setInputValues(prev => ({ ...prev, [axis]: value }));
  }, []);

  const commitAxis = useCallback(
    (axis: 'x' | 'y') => {
      const raw = inputValues[axis];
      const numDisplay = parseFloat(raw);

      if (!raw || isNaN(numDisplay) || numDisplay <= 0) {
        setInputValues({
          x: String(toDisplay(logo.size.x) ?? ''),
          y: String(toDisplay(logo.size.y) ?? ''),
        });
        return;
      }

      const universal = toUniversal(numDisplay);
      if (universal < MIN_LOGO_SIZE) {
        setInputValues({
          x: String(toDisplay(logo.size.x) ?? ''),
          y: String(toDisplay(logo.size.y) ?? ''),
        });
        return;
      }

      const ratio = logo.size.x / logo.size.y;
      const otherAxis = axis === 'x' ? 'y' : 'x';
      const otherUniversal = axis === 'x' ? universal / ratio : universal * ratio;

      dispatch(
        updateGraphicSize({
          id: logo.id,
          size: {
            x: axis === 'x' ? universal : otherUniversal,
            y: axis === 'y' ? universal : otherUniversal,
          },
        }),
      );

      setInputValues(prev => ({
        ...prev,
        [otherAxis]: String(toDisplay(otherUniversal) ?? ''),
      }));
    },
    [dispatch, logo.id, logo.size, inputValues, toDisplay, toUniversal],
  );

  const onInputFocus = useCallback((axis: 'x' | 'y') => {
    setFocusedAxis(axis);
  }, []);

  const onInputFocusOut = useCallback(
    (axis: 'x' | 'y') => {
      setFocusedAxis(null);
      commitAxis(axis);
    },
    [commitAxis],
  );

  const scaleSize = useCallback(
    (op: 'increase' | 'decrease') => {
      const multiplier = op === 'increase' ? SCALE_FACTOR : 1 / SCALE_FACTOR;
      const newSize = {
        x: logo.size.x * multiplier,
        y: logo.size.y * multiplier,
      };
      dispatch(updateGraphicSize({ id: logo.id, size: newSize }));
    },
    [dispatch, logo.id, logo.size],
  );

  const handleRegionChange = useCallback(
    (e: unknown) => {
      const value = (e as DropdownOption)?.value ?? String(e);
      const region = styleRegions.find(r => r.id === value);
      if (!region) {
        dispatch(updateGraphicRegion({ id: logo.id, region: value }));
        return;
      }
      const imageSizeFitted = calculateLogoSizeForRegion(logo.imageSize, region, is2DProduct);
      const transform = getInitialTransformFromRegion(region, imageSizeFitted, is2DProduct);
      dispatch(updateGraphicRegion({ id: logo.id, region: value }));
      dispatch(updateGraphicSize({ id: logo.id, size: imageSizeFitted }));
      dispatch(setGraphicTransform({ id: logo.id, transform }));
    },
    [dispatch, logo.id, logo.imageSize, styleRegions],
  );

  const handleTechniqueChange = useCallback(
    (e: unknown) => {
      const value = (e as DropdownOption)?.value ?? String(e);
      dispatch(updateGraphicTechnique({ id: logo.id, technique: value as LogoTechnique }));
    },
    [dispatch, logo.id],
  );

  const handleCustomTechniqueBlur = useCallback(() => {
    dispatch(updateGraphicCustomTechnique({ id: logo.id, customTechnique }));
  }, [dispatch, logo.id, customTechnique]);

  const showOutsideWarn = useMemo(() => {
    if (!logo || !styleRegions.length) return false;
    const region = styleRegions.find(r => r.id === logo.region);
    if (!region) return false;
    return !isLogoInside(
      logo.size,
      logo.transform,
      region.size as { x: number; y: number },
      region.transform as { translation: { x: number; y: number }; rotation: number },
    );
  }, [logo, styleRegions]);

  return {
    customTechnique,
    handleCustomTechniqueBlur,
    handleNameBlur,
    handleRegionChange,
    handleSizeInputChange,
    handleTechniqueChange,
    inputValues,
    onInputFocus,
    onInputFocusOut,
    regionOptions,
    setCustomTechnique,
    scaleSize,
    showOutsideWarn,
    unitSuffix,
  };
};
